import { constants } from "@/constants";
import messages from "@/i18n/messages.json";
import PageLoader from "@/services/pageLoader";
import {
  buildOrderPageRetryUrl,
  getRetryOrderId,
  hasRetryOrderId,
} from "@/services/orderPageRetryParam";
import PaymentMonitoring, { FLOW_STEPS } from "@/services/paymentMonitoring";
import { useToasterStore } from "@/store/toaster";

function getLangMessages() {
  const lang = window.globalConfig?.lang || "fr";
  return messages[lang]?.messages || messages.fr.messages;
}

function resolveRetryOrderId(urlParams) {
  const fromUrl = getRetryOrderId(urlParams);
  if (fromUrl) {
    return fromUrl;
  }

  try {
    const raw = localStorage.getItem(constants.PARAM_PLUS);
    if (!raw) {
      return null;
    }
    const paramPlus = JSON.parse(raw);
    if (paramPlus?.orderId) {
      return String(paramPlus.orderId).trim() || null;
    }
  } catch (_) {
    /* ignore malformed paramPlus */
  }

  return null;
}

function navigateToOrderPageRetry(orderId) {
  const slug = window.globalConfig?.orderPageSlug;
  if (!slug || !orderId) {
    return;
  }

  const extraParams = Object.fromEntries(
    new URL(window.location.href).searchParams.entries(),
  );
  delete extraParams.paymentKo;
  delete extraParams.alreadySubscribed;
  delete extraParams.retry;

  window.location.href = buildOrderPageRetryUrl(
    window.location.origin,
    slug,
    orderId,
    extraParams,
  );
}

function cleanOrderPageErrorQueryParams(urlParams) {
  const newUrl = new URL(window.location.href);
  let changed = false;

  if (urlParams.get("alreadySubscribed") === "1") {
    newUrl.searchParams.delete("alreadySubscribed");
    changed = true;
  }

  if (urlParams.get("paymentKo") === "1") {
    newUrl.searchParams.delete("paymentKo");
    changed = true;
  }

  if (
    newUrl.searchParams.has("retry") &&
    !getRetryOrderId(newUrl.searchParams)
  ) {
    newUrl.searchParams.delete("retry");
    changed = true;
  }

  if (changed) {
    window.history.replaceState({}, "", newUrl.toString());
  }
}

export function showOrderPageErrorModalsFromQuery() {
  if (!window.globalConfig?.isOrderPage) {
    return;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const langMessages = getLangMessages();

  PageLoader.hide();

  if (urlParams.get("alreadySubscribed") === "1") {
    useToasterStore().showBlockingModal({
      title: langMessages.alreadyClickedTitle,
      message: langMessages.alreadyClickedMessage,
      type: "danger",
    });

    PaymentMonitoring.trackFunnelEvent("already_subscribed_modal_view", {
      tags: {
        step: FLOW_STEPS.REDIRECT,
        order_status: "duplicate",
      },
    });
  }

  if (urlParams.get("paymentKo") === "1") {
    const retryOrderId = resolveRetryOrderId(urlParams);
    const modalOptions = {
      title: langMessages.paymentFailedTitle,
      message: langMessages.paymentFailedMessage,
      type: "danger",
    };

    if (retryOrderId) {
      modalOptions.primaryLabel = langMessages.retryPaymentButton;
      modalOptions.onPrimary = () => navigateToOrderPageRetry(retryOrderId);
    }

    useToasterStore().showBlockingModal(modalOptions);

    PaymentMonitoring.trackFunnelEvent("payment_failed_modal_view", {
      tags: {
        step: FLOW_STEPS.REDIRECT,
        order_status: "palyance_ko",
      },
      context: {
        orderId: retryOrderId,
        hasRetryParam: hasRetryOrderId(urlParams),
      },
    });
  }

  cleanOrderPageErrorQueryParams(urlParams);
}
